import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import * as lookupSlice from '../../../data/lookup.slice';
import * as clientSlice from '../../../data/client.slice';
import * as uiSlice from '../../../data/ui.slice';
import TextInput from '../../forms/TextInput';
import SelectMenu from '../../forms/SelectMenu';
import { ClientPurposeCode } from '../../../data/models/purpose-code-model.model';

const PREFIX = 'PurposeCodeDialog';

const classes = {
  content: `${PREFIX}-content`,
  message: `${PREFIX}-message`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(2),
    width: 300,
    gap: theme.spacing(2),
  },

  [`& .${classes.message}`]: {
    marginBottom: theme.spacing(2),
  },
}));

interface PurposeCodeDialogProps {
  clientBC: string;
  open: boolean;
  close: () => void;
}

export default function PurposeCodeDialog({
  clientBC,
  open,
  close,
}: PurposeCodeDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const requiredFieldMessage = t('common.requiredField');

  const purposeCodeCategories = useAppSelector(
    lookupSlice.selectPurposeCodeCategories,
  );
  const purposeCodeModelTypes = useAppSelector(
    lookupSlice.selectPurposeCodeModelTypes,
  );

  const savingPurposeCode = useAppSelector(clientSlice.selectSavingPurposeCode);
  const savedPurposeCode = useAppSelector(clientSlice.selectSavedPurposeCode);
  const saveSuccessMessage = useAppSelector(uiSlice.selectSuccessMessage);

  const formHook = useForm({
    defaultValues: {
      Name: '',
      BusinessCode: '',
      Category: '',
      PurposeCodeType: '',
    },
  });

  React.useEffect(() => {
    if (saveSuccessMessage && savedPurposeCode) {
      handleClose();
      dispatch(clientSlice.setSavedPurposeCode(false));
    }
  }, [saveSuccessMessage]);

  const handleSave = (data: any) => {
    const purposeCode: ClientPurposeCode = {
      clientBC,
      name: data.Name,
      purposeCodeCategoryBC: data.BusinessCode,
      purposeCodeTypeBC: data.PurposeCodeType,
    };
    dispatch(clientSlice.createClientPurposeCodeAsync(purposeCode));
  };

  const handleClose = () => {
    formHook.reset();
    close();
  };

  return (
    <RootDialog open={open} onClose={handleClose}>
      <DialogTitle>{t('client.addPurposeCode')}</DialogTitle>
      <DialogContent>
        <div className={classes.content}>
          <TextInput
            name="Name"
            label={t('client.purposeCodeName')}
            control={formHook.control}
            required={true}
            rules={{ required: requiredFieldMessage }}
            errors={formHook.formState.errors}
          />
          {/* <TextInput
          name="Description"
          label={t('client.purposeCodeDescription')}
          control={formHook.control}
          required={true}
          rules={{ required: requiredFieldMessage }}
          errors={formHook.formState.errors}
        /> */}
          <SelectMenu
            label={t('client.purposeCodeCategory')}
            name="BusinessCode"
            values={purposeCodeCategories}
            valueAttr="purposeCodeCategoryBC"
            labelAttr="purposeCodeCategoryName"
            control={formHook.control}
            required={true}
            rules={{ required: requiredFieldMessage }}
          />
          <SelectMenu
            label={t('client.purposeCodeType')}
            name="PurposeCodeType"
            values={purposeCodeModelTypes}
            valueAttr="purposeCodeModelTypeBC"
            labelAttr="purposeCodeModelTypeName"
            control={formHook.control}
            required={true}
            rules={{ required: requiredFieldMessage }}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="secondary"
          disabled={savingPurposeCode}
          onClick={handleClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!formHook.formState.isValid || savingPurposeCode}
          onClick={formHook.handleSubmit(handleSave)}>
          {t('common.ok')}
        </Button>
      </DialogActions>
    </RootDialog>
  );
}
